import Footer from '@react/shared/components/Footer';
import HeaderContainer from '@react/shared/components/Header/Header.container';
import Image from '@react/shared/components/Image';
import TabNavigation from '@react/shared/components/TabNavigation';
import { populate } from '@sly/core/constants/populate';
import cx from 'classnames';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import dynamic from 'next/dynamic';
import Helmet from 'next/head';
import { object } from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { host } from 'sly/config';
import HubDynamicContent from 'sly/hubPage/components/HubDynamicContent';
import { assetPath } from 'sly/lib/path';
import { Redirect } from 'sly/lib/react-router';
import ReviewedByBlock from 'sly/resourceCenter/components/ReviewedByBlock';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';
import Heading from 'sly/system/Heading';

const ContactUs = dynamic(() =>
  /* #__LOADABLE__ */ import(
    /* webpackChunkName: "marketingContactUsPage" */ './ContactUs'
  )
);
const HowItWorks = dynamic(() =>
  /* #__LOADABLE__ */ import(
    /* webpackChunkName: "marketingHowItWorksPage" */ './HowItWorks'
  )
);
const Press = dynamic(() =>
  /* #__LOADABLE__ */ import(
    /* webpackChunkName: "marketingPressPage" */ './Press'
  )
);

const Header = HeaderContainer;

const getComponentData = (data, value) =>
  data?.filter(({ __component }) => __component?.includes(value));

const MarketingPages = ({ params, path }) => {
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.Marketing, {});
  }, []);

  const {
    requestInfo: { result: res, hasFinished },
  } = usePrefetch('getMarketingPage', {
    'filters[slug][$eq]': params.marketingPage,
    populate: populate.marketingPage,
  });
  const result = res?.data;

  const linksList = useMemo(
    () =>
      getComponentData(result?.[0]?.MarketingPageDz, 'links-block')?.[0]?.link,
    [result]
  );
  const normalizeTitle = useMemo(() => result?.[0]?.title, [result]);
  const normalizeDescription = useMemo(
    () =>
      getComponentData(result?.[0]?.MarketingPageDz, 'links-block')?.[0]
        ?.description,
    [result]
  );
  const getImagePath = useMemo(() => result?.[0]?.mainImage?.path, [result]);
  const getListWithImg = useMemo(
    () => getComponentData(result?.[0]?.MarketingPageDz, 'list-with-img'),
    [result]
  );
  const getRecentArticlesContent = useMemo(
    () => getComponentData(result?.[0]?.MarketingPageDz, 'recent-articles'),
    [result]
  );
  const getArticlesArr = useMemo(
    () =>
      getRecentArticlesContent &&
      map(flatMap(getRecentArticlesContent, 'articles')),
    [getRecentArticlesContent]
  );
  const getListWithIcons = useMemo(
    () => getComponentData(result?.[0]?.MarketingPageDz, 'three-column-value'),
    [result]
  );
  const getFaqList = useMemo(
    () => getComponentData(result?.[0]?.MarketingPageDz, 'faq-block'),
    [result]
  );
  const infoBlockList = useMemo(
    () => getComponentData(result?.[0]?.MarketingPageDz, 'info-block'),
    [result]
  );
  if (!hasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-Flex-f6794776-16f7-4a19-9ce0-8ff4dcaeb720'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          data-tp-id='gen-Image-ff7db56e-3d95-422d-acf7-e0f82af156f3'
          alt='loader'
          layout='fill'
        />
      </div>
    );
  }
  if (hasFinished && !result?.length) {
    return <Redirect to='/' />;
  }

  const routes = [
    {
      slug: 'contact-us',
      component: (
        <ContactUs
          key={params.marketingPage}
          infoBlockList={infoBlockList}
          path={path}
        />
      ),
      exact: true,
    },
    {
      slug: 'how-it-works',
      component: (
        <HowItWorks
          key={params.marketingPage}
          imgWithItem={getListWithImg}
          getListWithIcons={getListWithIcons}
          getFaqList={getFaqList}
        />
      ),
      exact: true,
    },
    {
      slug: 'press',
      component: (
        <Press
          key={params.marketingPage}
          infoBlockList={infoBlockList}
          getListWithImg={getListWithImg}
          getRecentArticlesContent={getRecentArticlesContent}
          getArticlesArr={getArticlesArr}
        />
      ),
      exact: true,
    },
  ];

  const findSlugMatchingCmp = () =>
    hasFinished &&
    result?.length &&
    routes?.find((internalCmp) => internalCmp.slug === params.marketingPage);

  const cmp = findSlugMatchingCmp()?.component || (
    <div
      className={cx(
        'mx-m flex',
        'flex-col items-baseline justify-center',
        'md:mx-l',
        'lg:mx-0 lg:flex-row lg:items-start'
      )}
    >
      <HubDynamicContent
        content={result?.[0]?.MarketingPageDz?.filter(
          ({ __component }) => !__component?.includes('links-block')
        )}
        isMarketingPage={true}
      />
    </div>
  );

  const { reviewer } = result?.[0] || {};

  return (
    <>
      <Helmet>
        <title>{`${normalizeTitle} | Seniorly`}</title>
        <meta
          name='description'
          content={result?.[0]?.MarketingPageDz?.[0]?.description}
        />
        <link
          rel='canonical'
          href={`${host}/company/${params.marketingPage}`}
        />
      </Helmet>
      <Header />
      {!!linksList?.length && (
        <TabNavigation
          linksList={linksList}
          currentLink={params.marketingPage}
        />
      )}
      <div
        className={cx(
          'flex bg-viridian-lighter-90',
          'flex-col items-center justify-center',
          'text-center',
          'py-xxl px-m',
          'md:py-xxxl md:px-l',
          'lg:py-xxxl lg:px-0'
        )}
        data-tp-id='gen-Flex-1eee858b-e983-4f33-be90-8ed0a854098f'
      >
        {getImagePath && (
          <div
            className='relative mb-l aspect-1 h-[228px] w-full md:h-[478px] md:py-0 md:px-[4.583rem] lg:h-[335px] lg:w-[33.13rem] lg:p-0'
            data-tp-id='gen-div-bc9ffc20-0b43-4ffa-980f-ced0b6727f0f'
          >
            <Image
              objectFit='contain'
              path={getImagePath}
              priority
              loading='eager'
              alt={normalizeTitle}
              data-tp-id='gen-Image-ad57f5e8-d1d9-40c3-8172-e6162b0041af'
            />
          </div>
        )}
        <Heading
          {...{
            as: 'h1',
          }}
          className={cx(
            getImagePath ? 'font-t-l md:!font-t-xl' : '!font-t-xxl',
            'text-slate-base',
            'lg:w-[64.5rem]'
          )}
          data-tp-id='gen-Heading-7057edf0-847f-4976-b794-c3675c671f3a'
        >
          {getImagePath ? normalizeDescription : normalizeTitle}
        </Heading>
      </div>
      {cmp && cmp}

      {reviewer && (
        <>
          {params.marketingPage !== 'contact-us' &&
            params.marketingPage !== 'press' && (
              <hr className='my-xxl hidden md:block' />
            )}
          <div className='mx-m my-xxl md:mx-auto lg:w-[63rem]'>
            <ReviewedByBlock reviewer={reviewer} />
          </div>
        </>
      )}
      <Footer />
    </>
  );
};

MarketingPages.propTypes = {
  match: object,
};

export default MarketingPages;
